/*
* Shopify Optimized WebGL Fluid Simulation
* Based on original by Pavel Dobryakov
*/
'use strict';

// 1. Setup and Config
const canvas = document.getElementById('fluid');
const params = { alpha: true, depth: false, stencil: false, antialias: false, preserveDrawingBuffer: false };

let config = {
    SIM_RESOLUTION: 128,
    DYE_RESOLUTION: 512,
    DENSITY_DISSIPATION: 8, // Misty fade
    VELOCITY_DISSIPATION: 8,
    PRESSURE: 0.1,
    PRESSURE_ITERATIONS: 20,
    CURL: 0, // Swirly "Siberia Hills" look
    SPLAT_RADIUS: 0.01,
    SPLAT_FORCE: 6000,
    SHADING: true,
    COLORFUL: true,
    COLOR_UPDATE_SPEED: 10,
    PAUSED: false,
    BACK_COLOR: { r: 0, g: 0, b: 0 },
    TRANSPARENT: true, // Allows video to show through
    BLOOM: false, // Disabled for better performance
    SUNRAYS: false
};

function pointerPrototype () {
    this.id = -1;
    this.texcoordX = 0;
    this.texcoordY = 0;
    this.prevTexcoordX = 0;
    this.prevTexcoordY = 0;
    this.deltaX = 0;
    this.deltaY = 0;
    this.down = false;
    this.moved = false;
    this.color = [30, 0, 300];
}

let pointers = [new pointerPrototype()];
let splatStack = [];

// 2. WebGL Context Initialization
const { gl, ext } = getWebGLContext(canvas);

function getWebGLContext (canvas) {
    let gl = canvas.getContext('webgl2', params) || canvas.getContext('webgl', params) || canvas.getContext('experimental-webgl', params);
    const isWebGL2 = !!gl;

    let halfFloat = isWebGL2 ? gl.getExtension('EXT_color_buffer_float') : gl.getExtension('OES_texture_half_float');
    let supportLinearFiltering = isWebGL2 ? gl.getExtension('OES_texture_float_linear') : gl.getExtension('OES_texture_half_float_linear');

    const halfFloatTexType = isWebGL2 ? gl.HALF_FLOAT : halfFloat.HALF_FLOAT_OES;
    let formatRGBA = getSupportedFormat(gl, isWebGL2 ? gl.RGBA16F : gl.RGBA, gl.RGBA, halfFloatTexType);
    let formatRG = getSupportedFormat(gl, isWebGL2 ? gl.RG16F : gl.RGBA, isWebGL2 ? gl.RG : gl.RGBA, halfFloatTexType);
    let formatR = getSupportedFormat(gl, isWebGL2 ? gl.R16F : gl.RGBA, isWebGL2 ? gl.RED : gl.RGBA, halfFloatTexType);

    return { gl, ext: { formatRGBA, formatRG, formatR, halfFloatTexType, supportLinearFiltering } };
}

function getSupportedFormat (gl, internalFormat, format, type) {
    if (!supportRenderTextureFormat(gl, internalFormat, format, type)) {
        switch (internalFormat) {
            case gl.R16F: return getSupportedFormat(gl, gl.RG16F, gl.RG, type);
            case gl.RG16F: return getSupportedFormat(gl, gl.RGBA16F, gl.RGBA, type);
            default: return null;
        }
    }
    return { internalFormat, format };
}

function supportRenderTextureFormat (gl, internalFormat, format, type) {
    let texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, 4, 4, 0, format, type, null);
    let fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    return gl.checkFramebufferStatus(gl.FRAMEBUFFER) == gl.FRAMEBUFFER_COMPLETE;
}

// 3. Shaders
function compileShader (type, source) {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, source);
    gl.compileShader(shader);
    return shader;
}

function createProgram (vertexShader, fragmentShader) {
    let program = gl.createProgram();
    gl.attachShader(program, vertexShader);
    gl.attachShader(program, fragmentShader);
    gl.linkProgram(program);
    return program;
}

function getUniforms (program) {
    let uniforms = [];
    let uniformCount = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
    for (let i = 0; i < uniformCount; i++) {
        let uniformName = gl.getActiveUniform(program, i).name;
        uniforms[uniformName] = gl.getUniformLocation(program, uniformName);
    }
    return uniforms;
}

const baseVertexShader = compileShader(gl.VERTEX_SHADER, `
    precision highp float;
    attribute vec2 aPosition;
    varying vec2 vUv;
    varying vec2 vL, vR, vT, vB;
    uniform vec2 texelSize;
    void main () {
        vUv = aPosition * 0.5 + 0.5;
        vL = vUv - vec2(texelSize.x, 0.0);
        vR = vUv + vec2(texelSize.x, 0.0);
        vT = vUv + vec2(0.0, texelSize.y);
        vB = vUv - vec2(0.0, texelSize.y);
        gl_Position = vec4(aPosition, 0.0, 1.0);
    }
`);

const copyShader = compileShader(gl.FRAGMENT_SHADER, `
    precision mediump float;
    varying highp vec2 vUv;
    uniform sampler2D uTexture;
    void main () { gl_FragColor = texture2D(uTexture, vUv); }
`);

const clearShader = compileShader(gl.FRAGMENT_SHADER, `
    precision mediump float;
    varying highp vec2 vUv;
    uniform sampler2D uTexture;
    uniform float value;
    void main () { gl_FragColor = value * texture2D(uTexture, vUv); }
`);

const displayShader = compileShader(gl.FRAGMENT_SHADER, `
    precision highp float;
    varying vec2 vUv;
    uniform sampler2D uTexture;
    void main () {
        vec3 c = texture2D(uTexture, vUv).rgb;
        float a = max(c.r, max(c.g, c.b));
        gl_FragColor = vec4(c, a);
    }
`);

const splatShader = compileShader(gl.FRAGMENT_SHADER, `
    precision highp float;
    varying vec2 vUv;
    uniform sampler2D uTarget;
    uniform float aspectRatio;
    uniform vec3 color;
    uniform vec2 point;
    uniform float radius;
    void main () {
        vec2 p = vUv - point.xy;
        p.x *= aspectRatio;
        vec3 splat = exp(-dot(p, p) / radius) * color;
        vec3 base = texture2D(uTarget, vUv).xyz;
        gl_FragColor = vec4(base + splat, 1.0);
    }
`);

const advectionShader = compileShader(gl.FRAGMENT_SHADER, `
    precision highp float;
    varying vec2 vUv;
    uniform sampler2D uVelocity, uSource;
    uniform vec2 texelSize;
    uniform float dt, dissipation;
    void main () {
        vec2 coord = vUv - dt * texture2D(uVelocity, vUv).xy * texelSize;
        gl_FragColor = texture2D(uSource, coord) / (1.0 + dissipation * dt);
    }
`);

const divergenceShader = compileShader(gl.FRAGMENT_SHADER, `
    precision mediump float;
    varying highp vec2 vUv, vL, vR, vT, vB;
    uniform sampler2D uVelocity;
    void main () {
        float L = texture2D(uVelocity, vL).x;
        float R = texture2D(uVelocity, vR).x;
        float T = texture2D(uVelocity, vT).y;
        float B = texture2D(uVelocity, vB).y;
        gl_FragColor = vec4(0.5 * (R - L + T - B), 0.0, 0.0, 1.0);
    }
`);

const curlShader = compileShader(gl.FRAGMENT_SHADER, `
    precision mediump float;
    varying highp vec2 vL, vR, vT, vB;
    uniform sampler2D uVelocity;
    void main () {
        float L = texture2D(uVelocity, vL).y;
        float R = texture2D(uVelocity, vR).y;
        float T = texture2D(uVelocity, vT).x;
        float B = texture2D(uVelocity, vB).x;
        gl_FragColor = vec4(0.5 * (R - L - T + B), 0.0, 0.0, 1.0);
    }
`);

const vorticityShader = compileShader(gl.FRAGMENT_SHADER, `
    precision highp float;
    varying vec2 vUv, vL, vR, vT, vB;
    uniform sampler2D uVelocity, uCurl;
    uniform float curl, dt;
    void main () {
        float L = texture2D(uCurl, vL).x;
        float R = texture2D(uCurl, vR).x;
        float T = texture2D(uCurl, vT).x;
        float B = texture2D(uCurl, vB).x;
        float C = texture2D(uCurl, vUv).x;
        vec2 force = 0.5 * vec2(abs(T) - abs(B), abs(R) - abs(L));
        force /= length(force) + 0.0001;
        force *= curl * C;
        force.y *= -1.0;
        gl_FragColor = vec4(texture2D(uVelocity, vUv).xy + force * dt, 0.0, 1.0);
    }
`);

const pressureShader = compileShader(gl.FRAGMENT_SHADER, `
    precision mediump float;
    varying highp vec2 vUv, vL, vR, vT, vB;
    uniform sampler2D uPressure, uDivergence;
    void main () {
        float L = texture2D(uPressure, vL).x;
        float R = texture2D(uPressure, vR).x;
        float T = texture2D(uPressure, vT).x;
        float B = texture2D(uPressure, vB).x;
        float div = texture2D(uDivergence, vUv).x;
        gl_FragColor = vec4((L + R + B + T - div) * 0.25, 0.0, 0.0, 1.0);
    }
`);

const gradientSubtractShader = compileShader(gl.FRAGMENT_SHADER, `
    precision mediump float;
    varying highp vec2 vUv, vL, vR, vT, vB;
    uniform sampler2D uPressure, uVelocity;
    void main () {
        float L = texture2D(uPressure, vL).x;
        float R = texture2D(uPressure, vR).x;
        float T = texture2D(uPressure, vT).x;
        float B = texture2D(uPressure, vB).x;
        vec2 vel = texture2D(uVelocity, vUv).xy;
        gl_FragColor = vec4(vel - vec2(R - L, T - B), 0.0, 1.0);
    }
`);

// 4. Execution Logic
const copyProgram = createProgram(baseVertexShader, copyShader);
const copyUniforms = getUniforms(copyProgram);
const splatProgram = createProgram(baseVertexShader, splatShader);
const splatUniforms = getUniforms(splatProgram);
const advectionProgram = createProgram(baseVertexShader, advectionShader);
const advectionUniforms = getUniforms(advectionProgram);
const divergenceProgram = createProgram(baseVertexShader, divergenceShader);
const divergenceUniforms = getUniforms(divergenceProgram);
const curlProgram = createProgram(baseVertexShader, curlShader);
const curlUniforms = getUniforms(curlProgram);
const vorticityProgram = createProgram(baseVertexShader, vorticityShader);
const vorticityUniforms = getUniforms(vorticityProgram);
const pressureProgram = createProgram(baseVertexShader, pressureShader);
const pressureUniforms = getUniforms(pressureProgram);
const gradienSubtractProgram = createProgram(baseVertexShader, gradientSubtractShader);
const gradienSubtractUniforms = getUniforms(gradienSubtractProgram);
const displayProgram = createProgram(baseVertexShader, displayShader);
const displayUniforms = getUniforms(displayProgram);

function createFBO (w, h, internalFormat, format, type, param) {
    gl.activeTexture(gl.TEXTURE0);
    let texture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, param);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, param);
    gl.texImage2D(gl.TEXTURE_2D, 0, internalFormat, w, h, 0, format, type, null);
    let fbo = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
    return { texture, fbo, width: w, height: h, texelSizeX: 1/w, texelSizeY: 1/h, attach: (id) => { gl.activeTexture(gl.TEXTURE0 + id); gl.bindTexture(gl.TEXTURE_2D, texture); return id; }};
}

function createDoubleFBO (w, h, internalFormat, format, type, param) {
    let f1 = createFBO(w, h, internalFormat, format, type, param);
    let f2 = createFBO(w, h, internalFormat, format, type, param);
    return { width: w, height: h, texelSizeX: f1.texelSizeX, texelSizeY: f1.texelSizeY, get read() { return f1; }, set read(v) { f1 = v; }, get write() { return f2; }, set write(v) { f2 = v; }, swap() { let t = f1; f1 = f2; f2 = t; }};
}

function blit (target) {
    if (target == null) {
        gl.viewport(0, 0, gl.drawingBufferWidth, gl.drawingBufferHeight);
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    } else {
        gl.viewport(0, 0, target.width, target.height);
        gl.bindFramebuffer(gl.FRAMEBUFFER, target.fbo);
    }
    gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
}

// Global FBOs
let dye, velocity, divergence, curl, pressure;

function initFramebuffers () {
    let sim = getResolution(config.SIM_RESOLUTION);
    let dRes = getResolution(config.DYE_RESOLUTION);
    const texType = ext.halfFloatTexType;
    dye = createDoubleFBO(dRes.width, dRes.height, ext.formatRGBA.internalFormat, ext.formatRGBA.format, texType, gl.LINEAR);
    velocity = createDoubleFBO(sim.width, sim.height, ext.formatRG.internalFormat, ext.formatRG.format, texType, gl.LINEAR);
    divergence = createFBO(sim.width, sim.height, ext.formatR.internalFormat, ext.formatR.format, texType, gl.NEAREST);
    curl = createFBO(sim.width, sim.height, ext.formatR.internalFormat, ext.formatR.format, texType, gl.NEAREST);
    pressure = createDoubleFBO(sim.width, sim.height, ext.formatR.internalFormat, ext.formatR.format, texType, gl.NEAREST);
}

function getResolution (resolution) {
    let aspect = gl.drawingBufferWidth / gl.drawingBufferHeight;
    if (aspect < 1) aspect = 1 / aspect;
    let min = Math.round(resolution);
    let max = Math.round(resolution * aspect);
    return gl.drawingBufferWidth > gl.drawingBufferHeight ? { width: max, height: min } : { width: min, height: max };
}

// 5. Interaction and Animation
gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, -1,1, 1,1, 1,-1]), gl.STATIC_DRAW);
gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, gl.createBuffer());
gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, new Uint16Array([0,1,2, 0,2,3]), gl.STATIC_DRAW);
gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
gl.enableVertexAttribArray(0);

initFramebuffers();
let lastTime = Date.now();
update();

function update () {
    const dt = Math.min((Date.now() - lastTime) / 1000, 0.016);
    lastTime = Date.now();
    
    // Resize
    let w = Math.floor(canvas.clientWidth * window.devicePixelRatio);
    let h = Math.floor(canvas.clientHeight * window.devicePixelRatio);
    if (canvas.width != w || canvas.height != h) { canvas.width = w; canvas.height = h; initFramebuffers(); }

    // Physics
    gl.disable(gl.BLEND);
    curlProgram.bind; gl.useProgram(curlProgram);
    gl.uniform2f(curlUniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(curlUniforms.uVelocity, velocity.read.attach(0));
    blit(curl);

    gl.useProgram(vorticityProgram);
    gl.uniform2f(vorticityUniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(vorticityUniforms.uVelocity, velocity.read.attach(0));
    gl.uniform1i(vorticityUniforms.uCurl, curl.attach(1));
    gl.uniform1f(vorticityUniforms.curl, config.CURL);
    gl.uniform1f(vorticityUniforms.dt, dt);
    blit(velocity.write); velocity.swap();

    gl.useProgram(divergenceProgram);
    gl.uniform2f(divergenceUniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(divergenceUniforms.uVelocity, velocity.read.attach(0));
    blit(divergence);

    gl.useProgram(pressureProgram);
    gl.uniform2f(pressureUniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(pressureUniforms.uDivergence, divergence.attach(0));
    for (let i = 0; i < config.PRESSURE_ITERATIONS; i++) {
        gl.uniform1i(pressureUniforms.uPressure, pressure.read.attach(1));
        blit(pressure.write); pressure.swap();
    }

    gl.useProgram(gradienSubtractProgram);
    gl.uniform2f(gradienSubtractUniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    gl.uniform1i(gradienSubtractUniforms.uPressure, pressure.read.attach(0));
    gl.uniform1i(gradienSubtractUniforms.uVelocity, velocity.read.attach(1));
    blit(velocity.write); velocity.swap();

    gl.useProgram(advectionProgram);
    gl.uniform2f(advectionUniforms.texelSize, velocity.texelSizeX, velocity.texelSizeY);
    let vid = velocity.read.attach(0);
    gl.uniform1i(advectionUniforms.uVelocity, vid);
    gl.uniform1i(advectionUniforms.uSource, vid);
    gl.uniform1f(advectionUniforms.dt, dt);
    gl.uniform1f(advectionUniforms.dissipation, config.VELOCITY_DISSIPATION);
    blit(velocity.write); velocity.swap();

    gl.uniform1i(advectionUniforms.uVelocity, velocity.read.attach(0));
    gl.uniform1i(advectionUniforms.uSource, dye.read.attach(1));
    gl.uniform1f(advectionUniforms.dissipation, config.DENSITY_DISSIPATION);
    blit(dye.write); dye.swap();

    // Render
    gl.useProgram(displayProgram);
    gl.uniform1i(displayUniforms.uTexture, dye.read.attach(0));
    blit(null);

    requestAnimationFrame(update);
}

// 6. Input Listeners
function splat (x, y, dx, dy, color) {
    gl.useProgram(splatProgram);
    gl.uniform1i(splatUniforms.uTarget, velocity.read.attach(0));
    gl.uniform1f(splatUniforms.aspectRatio, canvas.width / canvas.height);
    gl.uniform2f(splatUniforms.point, x, y);
    gl.uniform3f(splatUniforms.color, dx, dy, 0.0);
    gl.uniform1f(splatUniforms.radius, 0.0005);
    blit(velocity.write); velocity.swap();
    gl.uniform1i(splatUniforms.uTarget, dye.read.attach(0));
    gl.uniform3f(splatUniforms.color, color.r, color.g, color.b);
    blit(dye.write); dye.swap();
}


// 1. Unified Move Handler
function handleMove(clientX, clientY, dx, dy) {
     // We use window.innerWidth/Height to match the 'fixed' canvas coordinates
    let posX = clientX / window.innerWidth;
    let posY = 1.0 - (clientY / window.innerHeight);
    
    splat(posX, posY, dx * 5, -dy * 5, {r: 0.2, g: 0.2, b: 0.2});
}


// 2. Mobile Touch Listeners
window.addEventListener('touchstart', e => {
    const touch = e.touches[0];
    window.lastTouchX = touch.clientX;
    window.lastTouchY = touch.clientY;
}, { passive: true });

window.addEventListener('touchmove', e => {
    const touch = e.touches[0];
    
    let dx = touch.clientX - (window.lastTouchX || touch.clientX);
    let dy = touch.clientY - (window.lastTouchY || touch.clientY);
    
    /* THE CORRECTION: Compress mobile touch acceleration using device pixel metrics */
    let mobileScaleForce = 0.2 / (window.devicePixelRatio || 1);
    let adjustedDx = dx * mobileScaleForce;
    let adjustedDy = dy * mobileScaleForce;
    
    handleMove(touch.clientX, touch.clientY, adjustedDx, adjustedDy);
    
    window.lastTouchX = touch.clientX;
    window.lastTouchY = touch.clientY;
}, { passive: true });

// 3. Desktop Mouse Listener
window.addEventListener('mousemove', e => {
    handleMove(e.clientX, e.clientY, e.movementX, e.movementY);
});